import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, Bell, BellRing, Heart, ExternalLink, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function PreordersPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/preorders");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load preorders");
    }
  };

  useEffect(() => { load(); }, []);

  const toggleNotify = async (release) => {
    setBusyId(release.release_id);
    try {
      const { data: resp } = await api.post(`/preorders/${release.release_id}/notify`);
      setData((d) => {
        if (!d) return d;
        const set = new Set(d.subscribed_release_ids);
        if (resp.subscribed) set.add(release.release_id); else set.delete(release.release_id);
        return { ...d, subscribed_release_ids: Array.from(set) };
      });
      toast.success(resp.subscribed ? "We'll notify you when it drops!" : "Notification removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update notification");
    } finally {
      setBusyId(null);
    }
  };

  const addToWishlist = async (release) => {
    setBusyId(release.release_id);
    try {
      await api.post("/wishlist", { release_id: release.release_id });
      toast.success(`${release.release_name} added to your wishlist`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't add to wishlist";
      // Already-on-wishlist errors are friendly noise; downgrade to info.
      if (typeof detail === "string" && detail.toLowerCase().includes("already")) {
        toast.info("Already on your wishlist");
      } else {
        toast.error(detail);
      }
    } finally {
      setBusyId(null);
    }
  };

  if (error) {
    return (
      <div data-testid="preorders-error" className="p-8 text-rose-400">{error}</div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="preorders-loading">
        <Loader2 className="w-10 h-10 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const subscribedSet = new Set(data.subscribed_release_ids);

  return (
    <div data-testid="preorders-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/catalog")}
            data-testid="preorders-back"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to catalog
          </button>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight flex items-center gap-3">
            <CalendarClock className="w-7 h-7 text-fuchsia-300" /> Future Releases
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {data.total} upcoming release{data.total === 1 ? "" : "s"} on the horizon. Subscribe and we'll ping you the moment they drop.
          </p>
        </div>
      </div>

      {data.items.length === 0 ? (
        <div data-testid="preorders-empty" className="rounded-2xl bg-gradient-to-br from-zinc-900 to-zinc-900/40 ring-1 ring-white/5 p-12 text-center">
          <Sparkles className="w-10 h-10 text-zinc-700 mx-auto" />
          <p className="text-zinc-300 font-semibold mt-4">No future releases right now</p>
          <p className="text-zinc-500 text-sm mt-1 max-w-sm mx-auto">
            When admins announce upcoming releases, they'll show up here so you can be first in line.
          </p>
          <Link
            to="/catalog"
            className="inline-flex items-center mt-6 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm"
          >
            Browse catalog →
          </Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="preorders-grid">
          {data.items.map((r) => (
            <PreorderCard
              key={r.release_id}
              release={r}
              subscribed={subscribedSet.has(r.release_id)}
              busy={busyId === r.release_id}
              onToggleNotify={() => toggleNotify(r)}
              onAddToWishlist={() => addToWishlist(r)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PreorderCard({ release, subscribed, busy, onToggleNotify, onAddToWishlist }) {
  const days = release.days_until_release;
  const dateStr = release.release_date ? new Date(release.release_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  // Color drift based on how close: rose <7d, amber <30d, fuchsia otherwise.
  const urgency =
    days != null && days <= 7
      ? { ring: "ring-rose-500/40 hover:ring-rose-400/60", glow: "from-rose-500/15 via-fuchsia-500/10 to-transparent", pill: "bg-rose-500/15 text-rose-200 border-rose-500/40" }
      : days != null && days <= 30
      ? { ring: "ring-amber-500/40 hover:ring-amber-400/60", glow: "from-amber-500/15 via-fuchsia-500/10 to-transparent", pill: "bg-amber-500/15 text-amber-200 border-amber-500/40" }
      : { ring: "ring-fuchsia-500/40 hover:ring-fuchsia-400/60", glow: "from-fuchsia-500/15 via-violet-500/10 to-transparent", pill: "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/40" };

  return (
    <li
      data-testid={`preorder-card-${release.release_id}`}
      className={`relative rounded-2xl ring-1 ${urgency.ring} bg-zinc-900/70 overflow-hidden group transition shadow-[0_0_24px_rgba(232,121,249,0.07)] hover:shadow-[0_0_32px_rgba(232,121,249,0.12)]`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${urgency.glow} pointer-events-none`} />
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none" />
      <div className="relative p-5 flex flex-col h-full">
        {/* Top: tier pill + countdown */}
        <div className="flex items-start justify-between gap-2">
          <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${urgency.pill}`}>
            ◐ Preorder
          </span>
          {days != null && (
            <div className="text-right">
              <div className="font-mono text-lg font-bold text-white leading-none">{days === 0 ? "today" : `${days}d`}</div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">
                {days === 0 ? "drops today" : "until release"}
              </div>
            </div>
          )}
        </div>

        {/* Image */}
        <Link to={`/catalog/${release.release_id}`} className="mt-3 block aspect-[4/3] rounded-xl bg-zinc-800/80 ring-1 ring-white/5 overflow-hidden">
          {release.model_image ? (
            <img src={release.model_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">no image yet</div>
          )}
        </Link>

        {/* Body */}
        <div className="mt-3 flex-1">
          <Link to={`/catalog/${release.release_id}`} className="font-semibold text-white text-sm leading-tight line-clamp-2 hover:text-fuchsia-200">
            {release.release_name}
          </Link>
          <p className="text-zinc-500 text-[11px] mt-1">
            {release.brand} · {release.vehicle}
          </p>
          {dateStr && (
            <p className="text-zinc-400 text-xs mt-2 flex items-center gap-1.5">
              <CalendarClock className="w-3 h-3 text-zinc-500" /> {dateStr}
            </p>
          )}
          {release.msrp != null && release.msrp > 0 && (
            <p className="text-zinc-500 text-xs mt-1">
              MSRP <span className="font-mono text-zinc-300">${release.msrp.toFixed(2)}</span>
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleNotify}
            disabled={busy}
            data-testid={`preorder-notify-${release.release_id}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition disabled:opacity-50 ${
              subscribed
                ? "bg-amber-500/15 border-amber-400/50 text-amber-200 hover:bg-amber-500/25"
                : "bg-zinc-900 border-zinc-700 text-zinc-200 hover:border-fuchsia-500/50 hover:text-fuchsia-200"
            }`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : subscribed ? <BellRing className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
            {subscribed ? "Subscribed" : "Notify me"}
          </button>
          <button
            type="button"
            onClick={onAddToWishlist}
            disabled={busy}
            data-testid={`preorder-wishlist-${release.release_id}`}
            title="Add to wishlist"
            className="px-3 py-2 rounded-lg text-xs font-semibold border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-rose-500/50 hover:text-rose-300 transition disabled:opacity-50"
          >
            <Heart className="w-3.5 h-3.5" />
          </button>
          {release.preorder_url && (
            <a
              href={release.preorder_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`preorder-link-${release.release_id}`}
              title="Open preorder page"
              className="px-3 py-2 rounded-lg text-xs font-semibold border border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-fuchsia-500/50 hover:text-fuchsia-200 transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
